/**
 * Language selector component.
 * @module components/LanguageSelector/LanguageSelector
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { useSelector } from 'react-redux';
import cx from 'classnames';
import { find, map } from 'lodash';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import langmap from '@plone/volto/helpers/LanguageMap/LanguageMap';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';

import config from '@plone/volto/registry';

import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  switchLanguageTo: {
    id: 'Switch to',
    defaultMessage: 'Switch to',
  },
});

// Map of language codes to flag emojis
const languageFlags = {
  en: '🇬🇧',
  de: '🇩🇪',
  es: '🇪🇸',
  fr: '🇫🇷',
  it: '🇮🇹',
  pt: '🇵🇹',
  'pt-br': '🇧🇷',
  nl: '🇳🇱',
  pl: '🇵🇱',
  ro: '🇷🇴',
  da: '🇩🇰',
  fi: '🇫🇮',
  sv: '🇸🇪',
  no: '🇳🇴',
  ru: '🇷🇺',
  ja: '🇯🇵',
  zh: '🇨🇳',
  ar: '🇸🇦',
  he: '🇮🇱',
  tr: '🇹🇷',
  eu: '🏴󠁥󠁳󠁰󠁶󠁿',
  ca: '🏴󠁥󠁳󠁣󠁴󠁿',
};

const LanguageSelector = (props) => {
  const intl = useIntl();
  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );

  const { settings } = config;

  return settings.isMultilingual ? (
    <div className="language-selector">
      {map(
        settings.supportedLanguages.filter(
          (lang) => toReactIntlLang(lang) !== currentLang,
        ),
        (lang) => {
          const translation = find(translations, { language: lang });
          return (
            <Link
              aria-label={`${intl.formatMessage(
                messages.switchLanguageTo,
              )} ${langmap[lang].nativeName.toLowerCase()}`}
              className={cx({
                selected: toReactIntlLang(lang) === currentLang,
              })}
              to={
                translation ? flattenToAppURL(translation['@id']) : `/${lang}`
              }
              title={langmap[lang].nativeName}
              onClick={() => {
                props.onClickAction();
              }}
              key={`language-selector-${lang}`}
            >
              {languageFlags[lang] || lang.toUpperCase()}
            </Link>
          );
        },
      )}
    </div>
  ) : (
    <Helmet>
      <html lang={toReactIntlLang(settings.defaultLanguage)} />
    </Helmet>
  );
};

LanguageSelector.propTypes = {
  onClickAction: PropTypes.func,
};

LanguageSelector.defaultProps = {
  onClickAction: () => {},
};

export default LanguageSelector;
